import { useCallback, useEffect, useRef, useState } from 'react';
import { backendApi } from '../lib/api';
import {
  attachmentBytesPath, formatBytes, isOpenableImageType,
  IMAGE_AUTOLOAD_MAX_BYTES, VIDEO_INLINE_MAX_BYTES,
  type StoredAttachment,
} from '../lib/ticketAttachments';

/** A download's temporary blob: URL is released this long after the click. */
export const DOWNLOAD_REVOKE_DELAY_MS = 10_000;

const BOX: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6,
  width: 320, maxWidth: '100%', minHeight: 120, borderRadius: 8, padding: 12,
  background: 'var(--color-surface-overlay)', textAlign: 'center',
};

/**
 * The files the backend lists for a ticket, shown WITHOUT any public link.
 * Each file's bytes are fetched through `backendApi` (the admin's own session)
 * and shown from a blob: URL that this component revokes when it goes away.
 * Nothing here ever renders a URL that came from ticket JSON.
 */
export function StoredAttachments({ ticketId, files }: { ticketId: string; files: StoredAttachment[] }) {
  if (files.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }} data-testid="ticket-attachments">
      <strong style={{ fontSize: 13 }}>Attachments ({files.length})</strong>
      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
        {files.map((f) => <StoredAttachmentItem key={`${ticketId}:${f.id}`} ticketId={ticketId} file={f} />)}
      </div>
    </div>
  );
}

type Phase = 'idle' | 'loading' | 'ready' | 'error';

function StoredAttachmentItem({ ticketId, file }: { ticketId: string; file: StoredAttachment }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [blobType, setBlobType] = useState('');
  const [err, setErr] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const alive = useRef(true);
  const urlRef = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const path = attachmentBytesPath(ticketId, file.id);
  const sizeText = formatBytes(file.size);

  const isImage = file.kind === 'image';
  // A video held in memory to play must be a sane size; anything bigger is download-only.
  const isVideo = file.kind === 'video' && !(file.size != null && file.size > VIDEO_INLINE_MAX_BYTES);
  const tooBigToPreview = file.kind === 'video' && !isVideo;
  const autoLoad = isImage && file.size != null && file.size <= IMAGE_AUTOLOAD_MAX_BYTES;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, []);

  const load = useCallback(async () => {
    setPhase('loading'); setErr('');
    try {
      const blob = await backendApi.getBlob(path);
      if (!alive.current) return; // closed while fetching: never create a URL nobody will revoke
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const u = URL.createObjectURL(blob);
      urlRef.current = u;
      setUrl(u); setBlobType(blob.type || ''); setPhase('ready');
    } catch (e: any) {
      if (!alive.current) return;
      setErr(e?.message || 'Could not load this file.'); setPhase('error');
    }
  }, [path]);

  // Images load themselves, but only once scrolled into view.
  useEffect(() => {
    if (!autoLoad) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { void load(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); void load(); }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [autoLoad, load]);

  /** Save the file: reuse the loaded blob URL, or fetch with auth and use a temporary one. */
  async function download() {
    setDownloading(true); setDlErr('');
    try {
      let href = urlRef.current;
      let temporary = false;
      if (!href) {
        const blob = await backendApi.getBlob(path);
        if (!alive.current) return;
        href = URL.createObjectURL(blob);
        temporary = true;
      }
      const a = document.createElement('a');
      a.href = href; a.download = file.name; a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a); a.click(); a.remove();
      if (temporary) { const t = href; setTimeout(() => URL.revokeObjectURL(t), DOWNLOAD_REVOKE_DELAY_MS); }
    } catch (e: any) {
      if (alive.current) setDlErr(e?.message || 'Could not download this file.');
    } finally {
      if (alive.current) setDownloading(false);
    }
  }

  const downloadBtn = (
    <button type="button" className="btn btn-sm" onClick={() => void download()} disabled={downloading}
      aria-label={`Download ${file.name}`}>
      {downloading ? 'Downloading…' : 'Download'}
    </button>
  );

  let media: React.ReactNode = null;
  if (phase === 'error') {
    media = (
      <div style={BOX} role="alert">
        <span>Could not load this file{err ? `: ${err}` : '.'}</span>
        <button type="button" className="btn btn-sm" onClick={() => void load()}>Retry</button>
      </div>
    );
  } else if (phase === 'loading') {
    media = <div style={BOX} role="status">Loading{sizeText ? ` (${sizeText})` : ''}…</div>;
  } else if (phase === 'ready' && url && isImage) {
    const img = (
      <img src={url} alt={file.name}
        style={{ display: 'block', maxWidth: 320, maxHeight: 240, borderRadius: 8, background: 'var(--color-surface-overlay)' }} />
    );
    // Opening a blob in its own tab runs it as this origin: raster types only.
    media = isOpenableImageType(blobType || file.type)
      ? <a href={url} target="_blank" rel="noopener noreferrer" title={`Open ${file.name} full size`}>{img}</a>
      : img;
  } else if (phase === 'ready' && url && isVideo) {
    media = (
      <video controls preload="metadata" src={url}
        style={{ display: 'block', maxWidth: '100%', maxHeight: 300, borderRadius: 8, background: '#000' }} />
    );
  } else if (phase === 'idle' && (isVideo || (isImage && !autoLoad))) {
    media = (
      <div style={BOX}>
        <button type="button" className="btn btn-sm" onClick={() => void load()}>
          {isVideo ? 'Load video' : 'Load image'}{sizeText ? ` (${sizeText})` : ''}
        </button>
      </div>
    );
  } else if (phase === 'idle' && isImage) {
    media = <div style={BOX} aria-hidden="true" />; // placeholder until it scrolls into view
  }

  return (
    <div ref={rootRef} style={{ maxWidth: '100%' }} data-testid="ticket-attachment">
      {media}
      <div className="small" style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>{file.name}</span>
        {sizeText && <span className="muted">· {sizeText}</span>}
        {downloadBtn}
      </div>
      {tooBigToPreview && <div className="muted small">Too large to preview here — download it instead.</div>}
      {dlErr && (
        <div role="alert" className="small" style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Could not download this file: {dlErr}</span>
          <button type="button" className="btn btn-sm" onClick={() => void download()}>Retry download</button>
        </div>
      )}
    </div>
  );
}
